import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { loginRoute, signupRoute } from '../../utils/appRoutes';

// The landing page's main navigation bar, shared by the landing page and the legal pages (so a visitor never loses the menu).
// `active` = the label of the menu item to highlight ("Home" on the landing page, nothing on the legal pages).
const C = {
  bg: "#f7f2ea",
  surface: "#ffffff",
  navBg: "rgba(247,242,234,0.86)",
  text: "#14161c", textSub: "#6b6459", textMuted: "#a19a8c",
  border: "#e6dfd0", borderSoft: "#efe7d8",
  orangeFrom: "#fbbf24", orangeTo: "#e4870e",
  orangeGlow: "rgba(228,135,14,0.25)",
  green: "#16a34a",
};

const primaryBtnSx = {
  fontWeight: 700, textTransform: "none", borderRadius: "99px",
  color: "#fff", background: C.text,
  transition: "all 0.2s ease",
  "&:hover": { background: "#000", transform: "translateY(-1px)" },
};

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Integrations", href: "/#integrations" },
  { label: "Features", href: "/#features" },
  { label: "Pricing", href: "/#pricing" },
  { label: "FAQ", href: "/#faq" },
  { label: "Contact", href: "/contact" },
];

// section id → menu label, for the scroll highlight on the landing page
const SECTIONS = [["home", "Home"], ["integrations", "Integrations"], ["features", "Features"], ["pricing", "Pricing"], ["faq", "FAQ"]];

export default function LandingNav({ active: activeProp = "" }) {
  // active="auto" (landing page): the menu item follows the section that is on screen while the visitor scrolls
  const [spy, setSpy] = useState("Home");
  useEffect(() => {
    if (activeProp !== "auto") return undefined;
    const onScroll = () => {
      let current = "Home";
      SECTIONS.forEach(([id, label]) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 140) current = label;
      });
      if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 4) current = "FAQ";   // the very bottom belongs to the last section
      setSpy(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("hashchange", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("hashchange", onScroll); };
  }, [activeProp]);
  const active = activeProp === "auto" ? spy : activeProp;
  return (
    <>
      {/* ══ NAVBAR ══ */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: { xs: 1.5, sm: 3, md: 6 }, py: { xs: 1.2, md: 2 }, gap: 1, background: C.navBg,
        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        borderBottom: `1px solid ${C.borderSoft}`, position: "sticky", top: 0, zIndex: 100,
        boxShadow: "0 2px 20px rgba(31,36,48,0.06)",
      }}>
        <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <Box component="img" src="/assests/website-logo.png" alt="MarketSync" sx={{ width: { xs: 30, sm: 40 }, height: "auto", display: "block", flexShrink: 0, filter: "drop-shadow(0 2px 6px rgba(20,22,28,0.18))" }} />
          <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "stretch" }}>
            <Typography sx={{ fontSize: { xs: "1.08rem", sm: "1.55rem" }, fontWeight: 800, letterSpacing: "-0.022em", lineHeight: 1.15, whiteSpace: "nowrap" }}>
              <Box component="span" sx={{ color: C.text }}>Market</Box>
              <Box component="span" sx={{ background: `linear-gradient(135deg, ${C.orangeFrom}, ${C.orangeTo})`, WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>Sync</Box>
            </Typography>
            <Typography sx={{
                              fontSize: { xs: "0.42rem", sm: "0.6rem" }, fontWeight: 700, color: C.textSub,
                              letterSpacing: { xs: "0.06em", sm: "0.14em" }, lineHeight: 1,
                              display: "flex", justifyContent: "space-between",
                              width: "100%", whiteSpace: "nowrap",
                            }}>
                              <Box component="span">Connect</Box>
                              <Box component="span" sx={{ color: C.orangeTo }}>•</Box>
                              <Box component="span">Manage</Box>
                              <Box component="span" sx={{ color: C.orangeTo }}>•</Box>
                              <Box component="span">Grow</Box>
            </Typography>
          </Box>
        </Link>

        <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map((n) => (
            <Box key={n.label} component={Link} to={n.href} sx={{
              textDecoration: "none", fontSize: "0.92rem", fontWeight: n.label === active ? 700 : 600,
              color: n.label === active ? C.orangeTo : C.text,
              borderBottom: n.label === active ? `2px solid ${C.orangeTo}` : "2px solid transparent",
              pb: 0.4, transition: "color 0.18s", "&:hover": { color: C.orangeTo },
            }}>{n.label}</Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, gap: { xs: 0.6, md: 2.2 } }}>
          <Link to={loginRoute()} style={{ textDecoration: "none" }}>
            <Button sx={{ fontWeight: 700, textTransform: "none", color: C.text, minWidth: "auto", px: { xs: 0.6, sm: 1 }, fontSize: { xs: "0.8rem", sm: "0.9rem" }, whiteSpace: "nowrap", "&:hover": { background: "transparent", color: C.orangeTo } }}>Login →</Button>
          </Link>
          <Link to={signupRoute()} style={{ textDecoration: "none" }}>
            <Button sx={{ ...primaryBtnSx, minWidth: "auto", whiteSpace: "nowrap", fontSize: { xs: "0.76rem", sm: "0.86rem" }, px: { xs: 1.6, md: 2.8 }, py: { xs: 0.7, md: 1 } }}>Get Started →</Button>
          </Link>
        </Box>
      </Box>
    </>
  );
}
